import { toast } from "react-toastify";

import {
  StartAgenticLogicRuleRunsListenRequestBodyType,
  StartAgenticLogicRuleRunsListenRequestPathParamsType,
  StartAgenticLogicRuleRunsListenResponseBodyType,
  StopAgenticLogicRuleRunsListenRequestPathParamsType,
} from "@intelligems/ig-types";
import { getMutationErrorMessage } from "@src/queryClient";
import { useMutation } from "@tanstack/react-query";

import { api } from "../api";
import { ApiRequest } from "../types/api";

// The Recent runs live-listen window (Intelligems staff only) — the shared
// live-runs listen contract mounted under the rule. The panel opens it on
// mount (default window) and closes it on unmount. Neither mutation
// invalidates anything: the panel holds the returned expiry itself and every
// polled list response refreshes it.

type ListenRequest<PathParams> = Pick<
  ApiRequest<unknown, PathParams>,
  "pathParams"
>;

type StartListenRequest =
  ListenRequest<StartAgenticLogicRuleRunsListenRequestPathParamsType> & {
    body?: Partial<StartAgenticLogicRuleRunsListenRequestBodyType>;
  };

const startAgenticLogicRuleRunsListen = async (payload: StartListenRequest) => {
  const resp = await api.post<
    StartAgenticLogicRuleRunsListenResponseBodyType,
    Partial<StartAgenticLogicRuleRunsListenRequestBodyType>
  >(
    `/v2/agentic-logic/rules/${payload.pathParams.ruleId}/runs/listen`,
    payload.body ?? {}
  );
  return resp.data;
};

export const useStartAgenticLogicRuleRunsListen = () =>
  useMutation<
    StartAgenticLogicRuleRunsListenResponseBodyType,
    Error,
    StartListenRequest,
    unknown
  >({
    mutationFn: async (data) => startAgenticLogicRuleRunsListen(data),
    onError: (error) => {
      const errorDetail = getMutationErrorMessage(error);
      toast.error(
        `Failed to start listening${errorDetail ? `. ${errorDetail}` : ""}`
      );
    },
  });

const stopAgenticLogicRuleRunsListen = async (
  payload: ListenRequest<StopAgenticLogicRuleRunsListenRequestPathParamsType>
): Promise<void> => {
  await api.delete<void>(
    `/v2/agentic-logic/rules/${payload.pathParams.ruleId}/runs/listen`
  );
};

export const useStopAgenticLogicRuleRunsListen = () =>
  useMutation<
    void,
    Error,
    ListenRequest<StopAgenticLogicRuleRunsListenRequestPathParamsType>,
    unknown
  >({
    mutationFn: async (data) => stopAgenticLogicRuleRunsListen(data),
    // A failed stop is not worth a toast: the window's TTL closes it anyway.
  });
